import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Brain,
  Command,
  Layers,
  LayoutGrid,
  Lightbulb,
  Sparkles,
  LayoutList,
  Wand2,
  Zap,
  ChevronRight,
} from 'lucide-react';
import { KhiimoriLogo } from '@/components/brand/KhiimoriLogo';
import { Button } from '@/components/ui/Button';
import { useHasHydrated, useIsAuthenticated } from '@/stores/authStore';
import { cn } from '@/utils';

const TOTAL_SKILLS = 31;

const SKILL_CATEGORIES = [
  { name: 'Generate', count: 4 },
  { name: 'Analyze', count: 7 },
  { name: 'Organize', count: 6 },
  { name: 'Predict', count: 3 },
  { name: 'Optimize', count: 5 },
  { name: 'Collaborate', count: 6 },
] as const;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  }),
};

const AGENT_SURFACES = [
  {
    icon: Wand2,
    title: 'Generate App',
    description: 'Turn a cluster of open tabs into a working web app — dashboards, comparisons, timelines, and more.',
    accent: 'from-violet-500/20 to-fuchsia-500/10',
    iconColor: 'text-primary-400',
  },
  {
    icon: Brain,
    title: 'Analyze Workspace',
    description: 'Surface patterns, relationships, and themes across everything you have open right now.',
    accent: 'from-cyan-500/20 to-violet-500/10',
    iconColor: 'text-cyan-400',
  },
  {
    icon: Lightbulb,
    title: 'Smart Suggestions',
    description: 'A unified, priority-weighted feed of what to do next — close duplicates, group tabs, follow threads.',
    accent: 'from-amber-500/20 to-orange-500/10',
    iconColor: 'text-amber-400',
  },
  {
    icon: LayoutGrid,
    title: 'Auto-Organize',
    description: 'Group by domain, suspend idle tabs, deduplicate, and smart-merge related sessions automatically.',
    accent: 'from-emerald-500/20 to-cyan-500/10',
    iconColor: 'text-emerald-400',
  },
  {
    icon: Zap,
    title: 'Predict Tabs',
    description: 'AI-predicted next tabs and smart preloading so the page you need is ready before you click.',
    accent: 'from-fuchsia-500/20 to-pink-500/10',
    iconColor: 'text-fuchsia-400',
  },
  {
    icon: Sparkles,
    title: 'Proactive Manage',
    description: 'Autonomous orchestration that keeps your workspace clean while you stay in flow.',
    accent: 'from-violet-500/20 to-indigo-500/10',
    iconColor: 'text-primary-300',
  },
];

const STEPS = [
  {
    step: '01',
    title: 'Install & sync',
    description: 'Connect the browser extension. Khiimori tracks your live tabs in real time — no manual exports.',
  },
  {
    step: '02',
    title: 'Let AI understand',
    description: 'Skills extract entities, map relationships, and build context from the pages you actually read.',
  },
  {
    step: '03',
    title: 'Act in one click',
    description: 'Generate apps, organize chaos, or run orchestration passes from the command palette or skill rails.',
  },
];

const HIGHLIGHTS = [
  { icon: Command, label: 'Cmd+K skill palette' },
  { icon: LayoutList, label: 'Live tab intelligence' },
  { icon: Layers, label: '8 unified hubs' },
  { icon: Brain, label: `${TOTAL_SKILLS} agent skills` },
];

function LandingBackground() {
  return (
    <>
      <div
        className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,var(--khiimori-glow-violet),transparent),radial-gradient(ellipse_60%_40%_at_90%_10%,var(--khiimori-glow-fuchsia),transparent),radial-gradient(ellipse_50%_30%_at_10%_90%,var(--khiimori-glow-cyan),transparent),linear-gradient(180deg,var(--khiimori-bg-primary)_0%,var(--khiimori-bg-secondary)_50%,var(--khiimori-bg-tertiary)_100%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none fixed inset-0 -z-10 bg-[linear-gradient(var(--khiimori-border)_1px,transparent_1px),linear-gradient(90deg,var(--khiimori-border)_1px,transparent_1px)] bg-[size:60px_60px] opacity-[var(--khiimori-grid-opacity)]"
        aria-hidden
      />
    </>
  );
}

function LandingNav() {
  return (
    <header className="sticky top-0 z-50 border-b border-khiimori-border/10 bg-khiimori-bg-primary/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-3 group">
          <KhiimoriLogo size={36} className="rounded-xl shadow-lg shadow-primary-500/20 transition-transform group-hover:scale-105" />
          <span className="text-lg font-semibold tracking-tight text-khiimori-text-primary">Khiimori</span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
          <a href="#features" className="text-sm text-khiimori-text-muted transition-colors hover:text-khiimori-text-primary">
            Features
          </a>
          <a href="#how-it-works" className="text-sm text-khiimori-text-muted transition-colors hover:text-khiimori-text-primary">
            How it works
          </a>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/login">
            <Button variant="ghost" size="sm">
              Sign in
            </Button>
          </Link>
          <Link to="/register">
            <Button variant="gradient" size="sm" rightIcon={<ArrowRight size={16} />}>
              Get started
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function HeroPreview() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 0.35, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative mx-auto mt-16 max-w-4xl"
    >
      <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-primary-500/20 via-fuchsia-500/10 to-cyan-500/20 blur-2xl" aria-hidden />
      <div className="glass-card relative overflow-hidden rounded-2xl border border-khiimori-border/20 p-1 shadow-2xl">
        <div className="flex items-center gap-2 border-b border-khiimori-border/10 bg-khiimori-bg-elevated/50 px-4 py-3">
          <div className="flex gap-1.5">
            <span className="h-3 w-3 rounded-full bg-red-400/80" />
            <span className="h-3 w-3 rounded-full bg-amber-400/80" />
            <span className="h-3 w-3 rounded-full bg-emerald-400/80" />
          </div>
          <span className="ml-2 text-xs text-khiimori-text-muted">Khiimori — Live workspace</span>
        </div>
        <div className="grid gap-0 sm:grid-cols-[180px_1fr]">
          <div className="hidden border-r border-khiimori-border/10 bg-khiimori-bg-secondary/50 p-4 sm:block">
            {['Home', 'Tabs', 'AI', 'Apps', 'Insights'].map((hub, i) => (
              <div
                key={hub}
                className={cn(
                  'mb-1 flex items-center gap-2 rounded-lg px-3 py-2 text-xs',
                  i === 2 ? 'bg-primary-500/15 text-primary-300' : 'text-khiimori-text-muted'
                )}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-current opacity-60" />
                {hub}
              </div>
            ))}
          </div>
          <div className="space-y-3 p-4 sm:p-6">
            <div className="flex flex-wrap gap-2">
              {['Population data', 'GDP research', 'AI in healthcare', 'Meeting prep'].map((tab) => (
                <span
                  key={tab}
                  className="rounded-lg border border-khiimori-border/15 bg-khiimori-bg-elevated/40 px-3 py-1.5 text-xs text-khiimori-text-secondary"
                >
                  {tab}
                </span>
              ))}
            </div>
            <div className="rounded-xl border border-primary-500/20 bg-primary-500/5 p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-primary-400">Smart suggestion</p>
              <p className="mt-1 text-sm text-khiimori-text-primary">
                Group 4 research tabs → generate comparison app
              </p>
              <div className="mt-3 flex gap-2">
                <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs text-white">Apply</span>
                <span className="rounded-md border border-khiimori-border/20 px-2.5 py-1 text-xs text-khiimori-text-muted">Dismiss</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Tabs tracked', value: '24' },
                { label: 'Entities', value: '186' },
                { label: 'Skills ready', value: String(TOTAL_SKILLS) },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg bg-khiimori-bg-elevated/30 p-3 text-center">
                  <p className="text-lg font-semibold text-khiimori-text-primary">{stat.value}</p>
                  <p className="text-[10px] text-khiimori-text-muted">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function LandingPage() {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const hasHydrated = useHasHydrated();

  useEffect(() => {
    if (hasHydrated && isAuthenticated) {
      navigate('/home', { replace: true });
    }
  }, [hasHydrated, isAuthenticated, navigate]);

  if (!hasHydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-khiimori-bg-primary">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen text-khiimori-text-primary">
      <LandingBackground />
      <LandingNav />

      <main>
        {/* Hero */}
        <section className="mx-auto max-w-6xl px-4 pb-20 pt-16 sm:px-6 sm:pt-24">
          <motion.div
            initial="hidden"
            animate="visible"
            className="mx-auto max-w-3xl text-center"
          >
            <motion.div custom={0} variants={fadeUp} className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary-500/25 bg-primary-500/10 px-4 py-1.5 text-sm text-primary-300">
              <Sparkles size={14} />
              AI-native browser workspace
            </motion.div>

            <motion.h1
              custom={1}
              variants={fadeUp}
              className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
            >
              Your tabs,{' '}
              <span className="bg-gradient-to-r from-primary-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
                understood
              </span>{' '}
              and organized
            </motion.h1>

            <motion.p custom={2} variants={fadeUp} className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-khiimori-text-muted sm:text-xl">
              Khiimori watches what you browse, extracts insight from open pages, and runs agent skills to organize,
              predict, and build — so research chaos becomes actionable work.
            </motion.p>

            <motion.div custom={3} variants={fadeUp} className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/register">
                <Button variant="gradient" size="lg" rightIcon={<ArrowRight size={18} />}>
                  Start for free
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="glass" size="lg">
                  Sign in to workspace
                </Button>
              </Link>
            </motion.div>

            <motion.div custom={4} variants={fadeUp} className="mt-10 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              {HIGHLIGHTS.map(({ icon: Icon, label }) => (
                <span key={label} className="flex items-center gap-2 text-sm text-khiimori-text-muted">
                  <Icon size={16} className="text-primary-400" />
                  {label}
                </span>
              ))}
            </motion.div>
          </motion.div>

          <HeroPreview />
        </section>

        {/* Features */}
        <section id="features" className="border-t border-khiimori-border/10 bg-khiimori-bg-secondary/30 py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-medium uppercase tracking-wider text-primary-400">Agent surfaces</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
                {TOTAL_SKILLS} skills. Six surfaces. One workspace.
              </h2>
              <p className="mt-4 text-khiimori-text-muted">
                Six top-level agent surfaces orchestrate {TOTAL_SKILLS} registered skills — discoverable from Cmd+K,
                runnable from contextual rails, and orchestrated autonomously when you need them.
              </p>
            </div>

            <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {AGENT_SURFACES.map((feature, i) => (
                <motion.article
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ delay: i * 0.06, duration: 0.45 }}
                  className="group glass-card rounded-2xl border border-khiimori-border/15 p-6 transition-all hover:border-primary-500/25 hover:shadow-lg hover:shadow-primary-500/5"
                >
                  <div className={cn('mb-4 inline-flex rounded-xl bg-gradient-to-br p-3', feature.accent)}>
                    <feature.icon size={22} className={feature.iconColor} />
                  </div>
                  <h3 className="text-lg font-semibold text-khiimori-text-primary">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-khiimori-text-muted">{feature.description}</p>
                </motion.article>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
              {SKILL_CATEGORIES.map((cat) => (
                <span
                  key={cat.name}
                  className="rounded-full border border-khiimori-border/20 bg-khiimori-bg-elevated/40 px-3 py-1.5 text-xs text-khiimori-text-secondary"
                >
                  <span className="font-medium text-khiimori-text-primary">{cat.name}</span>
                  <span className="ml-1.5 text-khiimori-text-muted">{cat.count}</span>
                </span>
              ))}
              <span className="rounded-full border border-primary-500/25 bg-primary-500/10 px-3 py-1.5 text-xs font-medium text-primary-300">
                {TOTAL_SKILLS} total
              </span>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-medium uppercase tracking-wider text-cyan-400">How it works</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
                From tab overload to clarity in minutes
              </h2>
            </div>

            <div className="mt-16 grid gap-8 md:grid-cols-3">
              {STEPS.map((item, i) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.45 }}
                  className="relative"
                >
                  <span className="text-5xl font-bold text-khiimori-border/40">{item.step}</span>
                  <h3 className="mt-2 text-xl font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-khiimori-text-muted">{item.description}</p>
                  {i < STEPS.length - 1 && (
                    <ChevronRight
                      size={20}
                      className="absolute right-0 top-8 hidden text-khiimori-text-muted md:block"
                      aria-hidden
                    />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-khiimori-border/10 py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="relative overflow-hidden rounded-3xl border border-primary-500/20 bg-gradient-to-br from-primary-500/15 via-fuchsia-500/10 to-cyan-500/10 p-10 text-center sm:p-16">
              <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary-500/20 blur-3xl" aria-hidden />
              <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-cyan-500/15 blur-3xl" aria-hidden />
              <div className="relative">
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                  Ready to tame your browser?
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-khiimori-text-muted">
                  Join Khiimori and turn the tabs you already have open into organized research, generated apps, and
                  proactive workspace management.
                </p>
                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <Link to="/register">
                    <Button variant="gradient" size="lg" rightIcon={<ArrowRight size={18} />}>
                      Create your account
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button variant="secondary" size="lg">
                      I already have an account
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-khiimori-border/10 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 sm:flex-row sm:px-6">
          <div className="flex items-center gap-2 text-sm text-khiimori-text-muted">
            <KhiimoriLogo size={24} className="rounded-md" />
            <span>© {new Date().getFullYear()} Khiimori</span>
          </div>
          <div className="flex gap-6 text-sm text-khiimori-text-muted">
            <Link to="/login" className="transition-colors hover:text-khiimori-text-primary">
              Sign in
            </Link>
            <Link to="/register" className="transition-colors hover:text-khiimori-text-primary">
              Register
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
